// Hook to get the PDF report from the server from anywhere on the page (report card, page title button, sidebar
// tools): captures the 3D views, sends the calculation's data and prints the PDF the server renders, in the language
// picked from the button's menu (see ReportLanguageMenu) - or hands it back as an object URL for the report card's
// preview.
import { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import type { LanguageCode } from "../../../types";
import { useScrewConnectorReportApi } from "../../../http/screwConnector/useReport";
import { loadPublicImageAsBase64 } from "../../../utils";
import { screwFamilyImage } from "./familyImages";
import type { ReportContext } from "./reportContext";
import { printReportPdf } from "./reportPdf";
import { buildReportPayload, type ReportReferencesInput, type ReportVersion, type ReportViews } from "./reportPayload";
import type { ReportImages } from "./view3dCapture";

export type ReportSource = {
  // the saved calculation (the server checks the user can read it); no report without it
  calculationId?: string;
  state: CalcState | null;
  blockReason?: string | null; // calcBlockReason (default: state.blocked)
  ctx: ReportContext;
  references: ReportReferencesInput;
  // the 3D views for the report (3D viewer's captureReportImages)
  captureImages?: () => Promise<ReportImages | null | undefined>;
};

export async function resolveReportViews(source: ReportSource): Promise<ReportViews> {
  if (!source.captureImages) return {};
  try {
    return (await source.captureImages()) ?? {};
  } catch {
    // a failed capture must never block the report
    return {};
  }
}

// Latest props in a ref, for async callbacks that must read the state as it is after an await.
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

export function useReportPdf(source: ReportSource) {
  const src = useLatest(source);
  const i18n = useI18n();
  const i18nRef = useLatest(i18n);
  const language = i18n.translation.languageCode;
  const dispatchRef = useLatest(useContext(NotificationDispatch));
  // useApi hands out new functions on every render: read through a ref, so print and preview stay the same functions
  // (the report card's refresh effects depend on them)
  const reportApi = useLatest(useScrewConnectorReportApi());
  const [isPrinting, setIsPrinting] = useState(false);
  const busy = useRef(false);

  // the request for version: the 3D views captured now unless given, the state read after the captures; in the user's
  // language unless given
  const reportData = useCallback(
    async (version: ReportVersion, views?: ReportViews, reportLanguage: LanguageCode = language) => {
      const captured = views ?? (await resolveReportViews(src.current));
      const { state, ctx, blockReason, references } = src.current;
      const photo = screwFamilyImage(state?.screwRec?.family ?? ctx.family);
      const screwImage = photo ? await loadPublicImageAsBase64(photo) : null;
      return buildReportPayload(state, ctx, {
        version,
        language: reportLanguage,
        blockReason,
        references,
        views: captured,
        screwImage,
      });
    },
    [src, language],
  );

  const print = useCallback(
    async (version: ReportVersion, reportLanguage?: LanguageCode) => {
      const calculationId = src.current.calculationId;
      if (busy.current || !calculationId) return;
      busy.current = true;
      setIsPrinting(true);
      let url: string | null = null;
      try {
        const data = await reportData(version, undefined, reportLanguage);
        url = await reportApi.current.getScrewConnectorReport(calculationId, data);
        await printReportPdf(url);
      } catch (err) {
        dispatchRef.current(showErrorNotification(i18nRef.current, err));
      } finally {
        if (url) URL.revokeObjectURL(url);
        busy.current = false;
        setIsPrinting(false);
      }
    },
    [src, i18nRef, reportData, reportApi, dispatchRef],
  );

  // the PDF as an object URL (the caller revokes it), null without a saved calculation
  const preview = useCallback(
    async (version: ReportVersion, views?: ReportViews, reportLanguage?: LanguageCode): Promise<string | null> => {
      const calculationId = src.current.calculationId;
      if (!calculationId) return null;
      return reportApi.current.getScrewConnectorReport(calculationId, await reportData(version, views, reportLanguage));
    },
    [src, reportData, reportApi],
  );

  return { print, isPrinting, preview };
}
